import React, { ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button, Input, InputRef, message, Modal, Popover, Space, Typography } from 'antd';
import { addSeconds, isAfter } from 'date-fns';

import { LanguageContext } from 'context/language';
import { getPinCode, removePinCode, setPinCode } from 'utils/storage';
import { currentUser } from 'utils/functions';
import { requestWithErrorLogging } from 'utils/request';
import { UserSettingsContext } from 'UserSettingsContext';

export const usePinCode = (props?: { title?: ReactNode; onSuccess?: () => void; onCancel?: () => void }) => {
  const { translate } = useContext(LanguageContext);
  const inputRef = useRef<InputRef>(null);

  const [showPinCodeModal, setShowPinCodeModal] = useState(false);
  const [showPinCodePopover, setShowPinCodePopover] = useState(false);
  const { showPinCode, setShowPinCode } = useContext(UserSettingsContext);
  const [value, setValue] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  // callers pass an inline object, so props is a new reference every render
  const propsRef = useRef(props);
  propsRef.current = props;

  // ref instead of state: form submit and modal OK can both fire before React commits
  const isVerifyingRef = useRef(false);

  const handleCloseModal = useCallback(() => {
    setShowPinCodeModal(false);
    propsRef.current?.onCancel?.();
  }, []);

  const handlePinCodeSubmit = useCallback(async () => {
    if (isVerifyingRef.current || !value.trim()) return;

    isVerifyingRef.current = true;
    setIsVerifying(true);

    try {
      const { data: isValid } = await requestWithErrorLogging<boolean>({
        url: '/profile-service/api/profile/pin-check',
        method: 'post',
        data: { pin: value },
      });

      if (isValid) {
        setShowPinCode(true);
        setPinCode({ [currentUser()]: addSeconds(new Date(), 900).toString() });
        propsRef.current?.onSuccess?.();
        setShowPinCodeModal(false);
        setShowPinCodePopover(false);
      } else {
        // keep modal open so the seller can retry without reopening it
        message.error(translate('wrong_pin_code_please_try_again'));
        setValue('');
        inputRef.current?.focus();
      }
    } catch {
      message.error(translate('error_occurred'));
    } finally {
      isVerifyingRef.current = false;
      setIsVerifying(false);
    }
  }, [setShowPinCode, translate, value]);

  const handleHidePinCode = useCallback(() => {
    setShowPinCode(false);
    removePinCode(currentUser());
  }, [setShowPinCode]);

  useEffect(() => {
    if (showPinCodeModal) {
      setValue('');
      if (inputRef?.current) {
        inputRef.current.focus();
      }
    }
  }, [showPinCodeModal]);

  useEffect(() => {
    const timeExpiry = getPinCode(currentUser());
    if (timeExpiry) {
      if (isAfter(new Date(timeExpiry), new Date())) return setShowPinCode(true);
      return handleHidePinCode();
    }
  }, [handleHidePinCode, setShowPinCode]);

  useEffect(() => {
    if (inputRef.current && showPinCodePopover) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [showPinCodePopover]);

  const modal = showPinCodeModal ? (
    <Modal
      title={props?.title}
      open={showPinCodeModal}
      onOk={handlePinCodeSubmit}
      onCancel={handleCloseModal}
      confirmLoading={isVerifying}
      okText={translate('submit')}
      cancelText={translate('cancel')}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handlePinCodeSubmit();
        }}
      >
        <Input
          ref={inputRef}
          placeholder={translate('pin_code')}
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      </form>
    </Modal>
  ) : null;

  const popover = (node: ReactNode) => {
    return (
      <Popover
        trigger="click"
        placement="bottomRight"
        open={showPinCodePopover}
        onOpenChange={setShowPinCodePopover}
        content={
          <div>
            <Space direction="vertical" className="w-full">
              {props?.title && <Typography>{props.title}</Typography>}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handlePinCodeSubmit();
                }}
              >
                <Input
                  placeholder={translate('enter_pin_code')}
                  autoFocus
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  ref={inputRef}
                />
              </form>
            </Space>
            <Space className="justify-end pt-3 px-4 mt-4 ml-[-1rem] mr-[-1rem] w-[-webkit-fill-available] w-[-moz-available] border-t">
              <Button onClick={() => setShowPinCodePopover(false)}>{translate('cancel')}</Button>
              <Button onClick={handlePinCodeSubmit} type="primary" loading={isVerifying}>
                {translate('submit')}
              </Button>
            </Space>
          </div>
        }
      >
        {node}
      </Popover>
    );
  };

  return { modal, popover, show: showPinCode, onShowModal: setShowPinCodeModal, onHide: handleHidePinCode };
};
